import PropTypes from 'prop-types';
import { forwardRef, useState, useRef, useImperativeHandle, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import styles from './FJTextAreaWidthInspire.module.less';
import InspireIcon from '../../../assets/svg/inspire-me.svg?react';
import FJTutorialIcon from '../../../assets/svg/tutorial.svg?react';
import FJLocalStore from '../../../store/FJLocalStore';

const FJTextAreaWidthInspire = forwardRef((props, ref) => {
    const {
        text,
        onBlur,
        disabled,
        maxLength,
        onChange,
        onKeyDown,
        readOnly,
        placeholder = 'Describe the image you want to generate',
        className,
        inputClassName,
        onFocus,
        getInspire,
        hasInspire = true,
    } = props;

    const [stateText, setStateText] = useState(text || '');
    const [isFocused, setIsFocused] = useState(false);
    const textareaRef = useRef(null);

    useImperativeHandle(ref, () => ({
        focus: () => {
            if (textareaRef.current) {
                textareaRef.current.focus();
            }
        },
        setText: text => {
            setStateText(text);
        },
        getTextarea: () => {
            return textareaRef.current;
        },
    }));

    useEffect(() => {
        setStateText(text);
    }, [text]);

    const _onBlur = e => {
        setIsFocused(false);
        if (onBlur) {
            onBlur(e);
        }
    };

    const _onFocus = e => {
        setIsFocused(true);
        if (onFocus) {
            onFocus(e);
        }
    };

    const _onKeyDown = useCallback(
        e => {
            if ((e.ctrlKey || e.metaKey) && e.keyCode === 65) {
                const value = textareaRef.current.value;
                textareaRef.current.value = '';
                textareaRef.current.value = value;
                textareaRef.current.select();
            }

            if (onKeyDown) {
                onKeyDown(e);
            }
        },
        [textareaRef, onKeyDown],
    );

    const _onChange = e => {
        setStateText(e.target.value);
        if (onChange) {
            onChange(e.target.value);
        }
    };

    const _onForbidDrag = e => {
        e.preventDefault();
        e.stopPropagation();
        return;
    };

    const _toGuide = () => {
        window.open(
            'https://help.flexclip.com/en/articles/10326783-how-to-write-effective-text-prompts-to-generate-ai-videos',
        );
    };

    const cnPanel = classNames(
        styles.textarea_box,
        isFocused ? styles.textarea_focus : '',
        disabled ? styles.textarea_disabled : '',
        className,
    );

    return (
        <div className={cnPanel}>
            <textarea
                className={classNames(styles.content_textarea, inputClassName)}
                ref={textareaRef}
                readOnly={readOnly}
                value={stateText}
                placeholder={placeholder}
                onBlur={_onBlur}
                onKeyDown={_onKeyDown}
                onChange={_onChange}
                onFocus={_onFocus}
                maxLength={maxLength}
                disabled={disabled}
                onDragStart={_onForbidDrag}
            />
            <div className={styles.bottom_btu_box}>
                {hasInspire ? (
                    <div className={styles.inspire_me} onClick={getInspire}>
                        <InspireIcon />
                        {FJLocalStore._('inspire-me')}
                    </div>
                ) : (
                    <div />
                )}
                <div className={styles.inspire_me} onClick={_toGuide}>
                    <FJTutorialIcon />
                    {FJLocalStore._('prompt-guide')}
                </div>
            </div>
        </div>
    );
});

FJTextAreaWidthInspire.displayName = 'FJTextAreaWidthInspire';

FJTextAreaWidthInspire.propTypes = {
    text: PropTypes.string,
    onBlur: PropTypes.func,
    disabled: PropTypes.bool,
    maxLength: PropTypes.number,
    onChange: PropTypes.func,
    onKeyDown: PropTypes.func,
    readOnly: PropTypes.bool,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    inputClassName: PropTypes.string,
    onFocus: PropTypes.func,
    showUploadImageAsPromptModal: PropTypes.func,
    getInspire: PropTypes.func,
    aiOptimizing: PropTypes.func,
    aiPromptDisabled: PropTypes.bool,
    hasInspire: PropTypes.bool,
};

export default FJTextAreaWidthInspire;
